// src/backendServices/mediaManagment/api.ts

import axios from 'axios';

import { API_URLS                        }  from '../urls';
import { BackendResponse                 ,
         handleError                     }  from '../../dataTypes/backendServicesManagment/types';
import { Media                           ,
         MediaDeletionPlan               }  from '../../dataTypes/mediaManagment/types';

import { getAudioSlideshowList           ,
         getAudioSlideshowById           ,
         updateAudioSlideshowVisibility  ,
         deleteAudioSlideshow            }  from './api_audioSlideshow';
import { getVideoList                    ,
         getVideoById                    ,
         updateVideoVisibility           ,
         deleteVideo                     }  from './api_video';
import { getImageList                    ,
         getImageById                    ,
         updateImageVisibility           ,
         deleteImage                     }  from './api_image';
import { getSoundList                    ,
         getSoundById                    ,
         updateSoundVisibility           ,
         deleteSound                     }  from './api_sound';

export interface BackendResponse_MediaList extends BackendResponse<Media[]> {}

/*
export const getMediaList = async (types:String[] = []): Promise<BackendResponse_MediaList> => {

  try
   {
    const [
             getImageListResponse          ,
             getSoundListResponse          ,
             getAudioSlideshowListResponse ,
             getVideoListResponse
          ]
          = await Promise.all ([
             getImageList          () ,
             getSoundList          () ,
             getAudioSlideshowList () ,
             getVideoList          ()
          ]);

    if (
            getAudioSlideshowListResponse.code !== 1
         || getVideoListResponse.code          !== 1
         || getImageListResponse.code          !== 1
         || getSoundListResponse.code          !== 1
       )
     {
      return handleError<Media[]>(-103, `Error retrieving media ${getAudioSlideshowListResponse.code} ${getVideoListResponse.code} ${getImageListResponse.code} ${getSoundListResponse.code}`);
     }


    const imageList          = getImageListResponse.payload
    const soundList          = getSoundListResponse.payload
    const audioSlideshowList = getAudioSlideshowListResponse.payload
    const videoList          = getVideoListResponse.payload // .map(video => ({ ...video, mediaType: 'video' }));
                                                   // inutile puisqu'on interroge le endpoint pour les vidéos
    const mediaList: Media[] = [
      ...imageList          ,
      ...soundList          ,
      ...audioSlideshowList ,
      ...videoList
      ].sort((a, b) => a.id - b.id);

    return {
      code    : 1,
      comment : 'The retrieval of media is complete.',
      payload : mediaList,
    };
   }
  catch (error)
   {
    return handleError<Media[]>(-104, 'Unknown error');
   }
};
*/


export const getMediaList = async (types: string[] = [], adminId?: number): Promise<BackendResponse_MediaList> => {
  try {
    // Si aucun type n'est spécifié ou si 'All' est inclus, on récupère tous les types
    const typesToFetch = types.length === 0 || types.includes('All') ? ['Image', 'Sound', 'Video', 'AudioSlideshow'] : types;

    // Créer un objet qui mappe les types de médias aux fonctions correspondantes.
    // ⚠️ La clé est le `mediaType` que rend l'API : 'Sound', pas 'Audio'. Seul
    // 'Audio' existait : getMediaList(['Sound']) appelait undefined(), l'exception
    // finissait en -104, et les sélecteurs de son (régie, diaporama) restaient vides.
    // 'Audio' reste accepté pour un appelant qui l'emploierait encore.
    const mediaTypeFunctions: Record<string, () => Promise<BackendResponse_MediaList>> = {
      Image: () => getImageList(false, adminId),
      Sound: () => getSoundList(adminId),
      Audio: () => getSoundList(adminId),
      Video: () => getVideoList(false, adminId),
      AudioSlideshow: () => getAudioSlideshowList(false, adminId)
    };

    // Préparer les promesses pour les types demandés — un type inconnu est ignoré
    // plutôt que de faire échouer toute la liste.
    const promises = typesToFetch.filter(type => type in mediaTypeFunctions).map(type => mediaTypeFunctions[type]());

    // Exécuter les promesses
    const responses = await Promise.all(promises);

    // Vérifier les codes de réponse
    const hasError = responses.some(response => response.code !== 1);
    if (hasError) {
      return handleError<Media[]>(-103, `Error retrieving media`);
    }

    // Combiner les résultats
    const mediaList: Media[] = responses.flatMap(response => response.payload).sort((a, b) => a.id - b.id);

    return {
      code: 1,
      comment: 'The retrieval of media is complete.',
      payload: mediaList,
    };
  } catch (error) {
    return handleError<Media[]>(-104, 'Unknown error');
  }
};


export interface BackendResponse_MediaList extends BackendResponse<Media[]> {}

export const getMediaListByType = async (mediaType: string, adminId?: number): Promise<BackendResponse_MediaList> => {

  switch ( mediaType )
   {
    case 'AudioSlideshow' : return getAudioSlideshowList (false, adminId)
    case 'Image'          : return getImageList (false, adminId)
    case 'Sound'          : return getSoundList (adminId)
    case 'Video'          : return getVideoList (false, adminId)
    default               : return handleError<Media[]>(-103, `Unkown type : ${mediaType}`);
   }
};

export interface BackendResponse_Media extends BackendResponse<Media> {}

export const getMediaById = async (mediaId: number): Promise<BackendResponse_Media> => {

  if ( mediaId === 0 || isNaN (mediaId) )
   {
    return handleError<Media>(-104, `mediaId incorrect`);

  }


  try
   {
    const getVideoByIdResponse = await getVideoById (mediaId)

    if ( getVideoByIdResponse.code === 1 )
     {
      return {
               code    : 1,
               comment : 'The retrieval of media is complete.',
               payload : getVideoByIdResponse.payload,
             };
     }

    const getAudioSlideshowByIdResponse = await getAudioSlideshowById (mediaId)

    if ( getAudioSlideshowByIdResponse.code === 1 )
     {
      return {
               code    : 1,
               comment : 'The retrieval of media is complete.',
               payload : getAudioSlideshowByIdResponse.payload,
             };
     }

    const getImageByIdResponse = await getImageById (mediaId)

    if ( getImageByIdResponse.code === 1 )
     {
      return {
               code    : 1,
               comment : 'The retrieval of media is complete.',
               payload : getImageByIdResponse.payload,
             };
     }

    const getSoundByIdResponse = await getSoundById (mediaId)

    if ( getSoundByIdResponse.code === 1 )
     {
      return {
               code    : 1,
               comment : 'The retrieval of media is complete.',
               payload : getSoundByIdResponse.payload,
             };
     }

    return handleError<Media>(-103, `Error retrieving media`);




    /*

    const [
             getImageByIdResponse          ,
             getSoundByIdResponse          ,
             getAudioSlideshowByIdResponse ,
             getVideoByIdResponse
          ]
          = await Promise.all ([
             getImageById          (mediaId) ,
             getSoundById          (mediaId) ,
             getAudioSlideshowById (mediaId) ,
             getVideoById          (mediaId)
          ]);

         if ( getAudioSlideshowByIdResponse.code === 1 )
          {
           return {
                    code    : 1,
                    comment : 'The retrieval of media is complete.',
                    payload : getAudioSlideshowByIdResponse.payload,
                  };
          }

    else if ( getVideoByIdResponse.code === 1 )
          {
           return {
                    code    : 1,
                    comment : 'The retrieval of media is complete.',
                    payload : getVideoByIdResponse.payload,
                  };
          }

    else if ( getImageByIdResponse.code === 1 )
          {
           return {
                    code    : 1,
                    comment : 'The retrieval of media is complete.',
                    payload : getImageByIdResponse.payload,
                  };
          }

    else if ( getSoundByIdResponse.code === 1 )
          {
           return {
                    code    : 1,
                    comment : 'The retrieval of media is complete.',
                    payload : getSoundByIdResponse.payload,
                  };
          }

    else  {
           return handleError<Media>(-103, `Error retrieving media`);
          }
    */
   }
  catch (error)
   {
    return handleError<Media>(-104, 'Unknown error');
   }
};

export const updateMediaVisibility = async (media: Media, state: number): Promise<BackendResponse> => {

  switch ( media.mediaType )
   {
    case 'AudioSlideshow' : return updateAudioSlideshowVisibility ( media.id, state )
    case 'Image'          : return updateImageVisibility          ( media.id, state )
    case 'Sound'          : return updateSoundVisibility          ( media.id, state )
    case 'Video'          : return updateVideoVisibility          ( media.id, state )
    default               : return handleError<void>(-103, `Type inconnu ${media.mediaType}`);
  }

}


export const deleteMedia = async ( media    : Media  ,
                                   adminId? : number ): Promise<BackendResponse> => {

  switch ( media.mediaType )
   {
    case 'AudioSlideshow' : return deleteAudioSlideshow ( media.id , adminId )
    case 'Image'          : return deleteImage          ( media.id , adminId )
    case 'Sound'          : return deleteSound          ( media.id , adminId )
    case 'Video'          : return deleteVideo          ( media.id , adminId )
    // Le commentaire d'un -103 est affiché tel quel à l'utilisateur depuis MediaList : il
    // doit rester une phrase lisible, même pour ce cas qui ne devrait jamais survenir.
    default               : return handleError<void>(-103, `Impossible de supprimer ce média : type inconnu (${media.mediaType}).`);
  }

}


/**
 * Ce que la suppression de ce média fera, demandé avant d'afficher la confirmation.
 *
 * Derrière le même bouton se cachent trois conséquences très différentes : tout
 * effacer, n'effacer que les fichiers en gardant la fiche, ou ne rien pouvoir faire.
 * L'utilisateur doit savoir laquelle il déclenche AVANT de cliquer sur Oui.
 *
 * Le serveur recalcule ce plan au moment de la suppression : celui-ci sert à informer,
 * jamais à décider.
 */
export const getMediaDeletionPlan = async ( media : Media ): Promise<BackendResponse<MediaDeletionPlan>> => {

  const url = `${API_URLS.getMediaDeletionPlan}`.replace('{id}', `${media.id}`);

  try
   {
    const response = await axios.get(url, { headers: { 'Accept': 'application/json' } });

    return {
      code    : 1                ,
      comment : ''               ,
      payload : response.data as MediaDeletionPlan
    };
   }
  catch (error)
   {
    if (axios.isAxiosError(error))
     {
      return handleError<MediaDeletionPlan>(-101, error.response?.data?.error ?? error.message);
     }

    return handleError<MediaDeletionPlan>(-102, 'Unknown error');
   }
}



export async function getVerifiedMedia(urls: string[]): Promise<string | null> {
  for (const url of urls) {
    try {
      const response = await fetch(url, { method: "HEAD" });

      if (response.ok) {
        return url;
      }
    } catch {
      // ignore et continue
    }
  }

  return null;
}
